import { screen, colors, fonts } from 'components/styles/variables';
import EmailField from 'components/ContentModal/EmailField';
import { ChangeEvent, useRef } from 'react';
import { Field, FieldProps, Form, Formik, FormikErrors, yupToFormErrors } from 'formik';
import { useTranslation } from 'react-i18next';
import { useValidation } from 'lib/hooks/useValidation';
import { buildEmailFormSchema } from 'lib/validationSchemas';
import emailFieldTapEvent from 'lib/Tracking/ContentModal/EmailFieldTap';
import useEvents from 'lib/hooks/useEvents';

interface EmailSubmitCardProps {
  onSubmit: (fields: { email: string }) => void;
  onEmailFieldChange: (e: ChangeEvent<HTMLInputElement>) => void;
}

export default function EmailSubmitCard({
  onSubmit,
  onEmailFieldChange,
}: EmailSubmitCardProps): JSX.Element {
  const { t } = useTranslation();
  const formRef = useRef<any>();
  const { validateEmail } = useValidation();
  const schema = buildEmailFormSchema({ emailValidator: validateEmail });
  const logger = useEvents();
  return (
    <div className="card" data-testid="submit-card">
      <div className="title">{t('contentModal.title')}</div>
      <div className="text">{t('contentModal.subheader')}</div>
      <Formik
        initialValues={{ email: '' }}
        validationSchema={schema}
        validate={async (values): Promise<void | FormikErrors<unknown>> => {
          try {
            await schema.validate(values, {
              abortEarly: false,
            });
            return undefined;
          } catch (err) {
            return yupToFormErrors(err);
          }
        }}
        innerRef={formRef}
        onSubmit={(values): void => {
          onSubmit(values);
        }}
      >
        <Form noValidate={true}>
          <div className="email-field">
            <Field name="email" autocomplete="on">
              {({ field, form }: FieldProps): JSX.Element => {
                const validationError = t(form.errors.email as string);
                const isTouched = form.touched.email;
                const { value, ...rest } = field;
                return (
                  <EmailField
                    data-testid="email-input"
                    error={validationError}
                    showValidationIcon={isTouched as boolean}
                    label="Email address"
                    {...rest}
                    value={value || ''}
                    onClick={(): void => {
                      logger.log(emailFieldTapEvent(window.location.href));
                    }}
                    onChange={(e: ChangeEvent<HTMLInputElement>): void => {
                      field.onChange(e);
                      onEmailFieldChange(e);
                    }}
                  />
                );
              }}
            </Field>
          </div>
          <EmailSubmitButton />
        </Form>
      </Formik>
      <style jsx>
        {`
          .card {
            padding: 61px 24px 0px;
            text-align: center;
          }

          .title {
            margin-bottom: 12px;
            font-weight: 700;
            font-size: 28px;
            line-height: 34px;
            letter-spacing: 0.2px;
          }

          .text {
            margin-bottom: 17px;
            font-weight: 400;
            font-size: 16px;
            letter-spacing: 0.2px;
          }

          .email-field {
            margin-bottom: 24px;
          }

          @media (min-width: ${screen.smMin}) {
            .card-image img {
              display: block;
              width: 368px;
            }

            .mobile-image {
              display: none;
            }

            .card {
              width: 368px;
              padding: 34px 40px 0px;
              text-align: left;
            }

            .title {
              margin-bottom: 12px;
              font-weight: 700;
              font-size: 30px;
              line-height: 40px;
              letter-spacing: 0.2px;
            }

            .text {
              margin-bottom: 23px;
              font-weight: 400;
              font-size: 16px;
              letter-spacing: 0.2px;
              line-height: 30px;
            }
          }
        `}
      </style>
    </div>
  );
}

function EmailSubmitButton(): JSX.Element {
  const { t } = useTranslation();
  return (
    <>
      <button type="submit" data-testid="content-modal-button">
        {t('contentModal.buttonText')}
      </button>
      <style jsx>
        {`
          button {
            width: 256px;
            padding: 10px 26px 12px;
            background: #f20902;
            border: none;
            border-radius: 4px;
            font-weight: 600;
            font-size: 16px;
            line-height: 22px;
            text-align: center;
            letter-spacing: 0.2px;
            color: ${colors.WHITE};
            font-family: ${fonts.default};
          }

          button:hover {
            background-color: ${colors.ALR2};
          }

          @media (min-width: ${screen.smMin}) {
            button {
              width: 180px;
            }
          }
        `}
      </style>
    </>
  );
}
